import { useMemo } from 'react';
import type { SystemHealthService, ServiceAvailabilityPoint } from '@/mocks/saSystemHealthCenter';
import { statusMeta } from '@/mocks/saSystemHealthCenter';

interface Props {
  service: SystemHealthService;
  onClose: () => void;
}

export default function AvailabilityChart({ service }: Props) {
  const data = service.availabilityHistory;

  const chartConfig = useMemo(() => {
    if (data.length === 0) return { bars: [], maxVal: 100, minVal: 0 };
    const values = data.map((d) => d.value);
    const maxVal = 100;
    const minVal = Math.floor(Math.min(...values) - 0.5);

    const bars = data.map((point, i) => {
      const height = ((point.value - minVal) / (maxVal - minVal)) * 100;
      let color = 'bg-emerald-400';
      if (point.value < 99.0) color = 'bg-red-400';
      else if (point.value < 99.7) color = 'bg-amber-400';
      const isLast = i === data.length - 1;
      const label = new Date(point.date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
      const showLabel = i % 5 === 0 || isLast;
      return { ...point, height, color, label, showLabel };
    });

    return { bars, maxVal, minVal };
  }, [data]);

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-foreground-400 text-xs">Sin datos de disponibilidad histórica</div>
    );
  }

  const meta = statusMeta[service.status];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${meta.dot}`}></div>
          <span className="text-foreground-800 text-xs font-semibold">Disponibilidad 30 días</span>
        </div>
        <span className="text-foreground-400 text-[10px]">Actual: {service.availability.toFixed(2)}%</span>
      </div>

      <div className="bg-background-100 rounded-xl p-4">
        <div className="flex items-end gap-[2px] h-32">
          {chartConfig.bars.map((bar, i) => (
            <div
              key={bar.date}
              className="flex-1 flex flex-col items-center justify-end min-w-0 group relative"
              style={{ height: '100%' }}
            >
              <div
                className={`w-full rounded-t-sm transition-all ${bar.color}`}
                style={{ height: `${Math.max(bar.height, 2)}%` }}
              ></div>
              {bar.showLabel && (
                <span className="text-foreground-300 text-[8px] mt-1.5 absolute -bottom-4 whitespace-nowrap">
                  {bar.label}
                </span>
              )}
              {/* Tooltip */}
              <div className="absolute bottom-full mb-1 hidden group-hover:block z-10">
                <div className="bg-foreground-900 text-background-50 text-[9px] px-2 py-1 rounded-md whitespace-nowrap">
                  {bar.date}: {bar.value.toFixed(2)}%
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-center justify-between text-[9px] text-foreground-400">
          <span>{chartConfig.minVal.toFixed(1)}%</span>
          <span className="text-emerald-500 font-medium">100.00%</span>
          <span>{chartConfig.minVal.toFixed(1)}%</span>
        </div>
      </div>

      <div className="flex items-center gap-3 text-[10px] text-foreground-500 justify-center">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-emerald-400"></span>
          &ge; 99.7%
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-amber-400"></span>
          99.0% - 99.7%
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-red-400"></span>
          &lt; 99.0%
        </span>
      </div>
    </div>
  );
}